/**
 * F44 (Wave 2D) — admin finance alert-rules page.
 *
 * Route: `/finance/alert-rules`. Permission: `financial:read` (view). Editing
 * a rule additionally needs `financial:export` (config changes are sensitive) —
 * the in-page check `adminUser.permissions.includes('financial:export')` renders
 * the rules read-only otherwise.
 *
 * The 4 rules from GET /admin/financial/alert-rules:
 *   - large_refund_processed     → thresholdMinor (CurrencyDisplay-paired NumberInput)
 *   - ar_aging_threshold_crossed → thresholdDays (NumberInput)
 *   - daily_finance_summary      → summaryHour (NumberInput 0–23)
 *   - weekly_finance_digest      → summaryHour + digestDow (0=Sun..6=Sat —
 *     honored by the worker; mirrors the F45 analytics alert-rules page)
 * Each carries an enable/disable Switch + a Save → PATCH /admin/financial/alert-rules/:ruleType.
 */
import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  CurrencyDisplay,
  NumberInput,
  Skeleton,
  Switch,
  useToast,
} from '@bravemove/ui-web';
import type {
  FinanceAlertRule,
  FinanceAlertType,
  UpdateFinanceAlertRuleInput,
} from '@bravemove/types';
import { adminFinancialApi } from '../../api/financial';
import { useAuth } from '../../auth/useAuth';
import { PageHeader } from '../../components/PageHeader';
import { FinanceNav } from './FinanceNav';

export function FinanceAlertRulesPage() {
  const { t } = useI18n();
  const { adminUser } = useAuth();
  const canEdit = adminUser?.permissions.includes('financial:export') ?? false;

  const [rules, setRules] = useState<readonly FinanceAlertRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRules(await adminFinancialApi.listAlertRules());
    } catch {
      setError(t('admin:financial.errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchRules();
  }, [fetchRules]);

  const onSaved = (rule: FinanceAlertRule) => {
    setRules((prev) => prev.map((r) => (r.ruleType === rule.ruleType ? rule : r)));
  };

  return (
    <div className="space-y-6" data-testid="finance-alert-rules-page">
      <PageHeader
        eyebrow="Finance"
        title={t('admin:financial.alertRules.title')}
        subtitle={t('admin:financial.alertRules.subtitle')}
      />
      <FinanceNav />

      {!canEdit ? (
        <div data-testid="finance-alert-rules-readonly-notice">
          <Banner tone="info">{t('admin:financial.alertRules.readonlyNotice')}</Banner>
        </div>
      ) : null}

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="finance-alert-rules-loading">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-28 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {rules.map((rule) => (
            <AlertRuleCard key={rule.ruleType} rule={rule} canEdit={canEdit} onSaved={onSaved} />
          ))}
        </div>
      )}
    </div>
  );
}

function AlertRuleCard({
  rule,
  canEdit,
  onSaved,
}: {
  rule: FinanceAlertRule;
  canEdit: boolean;
  onSaved: (rule: FinanceAlertRule) => void;
}) {
  const { t, locale } = useI18n();
  const { push } = useToast();

  const [enabled, setEnabled] = useState(rule.enabled);
  const [thresholdMinor, setThresholdMinor] = useState(rule.thresholdMinor ?? 0);
  const [thresholdDays, setThresholdDays] = useState(rule.thresholdDays ?? 0);
  const [summaryHour, setSummaryHour] = useState(rule.summaryHour ?? 0);
  // 0=Sun..6=Sat; the worker defaults to Monday when unset.
  const [digestDow, setDigestDow] = useState(rule.digestDow ?? 1);
  const [saving, setSaving] = useState(false);

  const showThresholdMinor = rule.ruleType === 'large_refund_processed';
  const showThresholdDays = rule.ruleType === 'ar_aging_threshold_crossed';
  const isDigest = rule.ruleType === 'weekly_finance_digest';
  const showSummaryHour = rule.ruleType === 'daily_finance_summary' || isDigest;

  const onSave = async () => {
    setSaving(true);
    try {
      const input: UpdateFinanceAlertRuleInput = {
        enabled,
        ...(showThresholdMinor ? { thresholdMinor } : {}),
        ...(showThresholdDays ? { thresholdDays } : {}),
        ...(showSummaryHour ? { summaryHour } : {}),
        ...(isDigest ? { digestDow } : {}),
      };
      const next = await adminFinancialApi.updateAlertRule(
        rule.ruleType as FinanceAlertType,
        input,
      );
      onSaved(next);
      push({ tone: 'success', title: t('admin:financial.alertRules.saved') });
    } catch {
      push({ tone: 'danger', title: t('admin:financial.alertRules.saveError') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="flex flex-col gap-4 rounded-xl border border-hairline bg-surface p-4"
      data-testid={`finance-alert-rule-${rule.ruleType}`}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-[14px] font-bold text-ink">
          {t(`admin:financial.alertRules.rule.${rule.ruleType}`)}
        </h2>
        <Switch
          label={t('admin:financial.alertRules.field.enabled')}
          checked={enabled}
          disabled={!canEdit}
          onChange={(e) => setEnabled(e.target.checked)}
          data-testid={`finance-alert-enabled-${rule.ruleType}`}
        />
      </div>

      {showThresholdMinor ? (
        <div>
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">
            {t('admin:financial.alertRules.field.thresholdMinor')}
          </label>
          <div className="flex items-center gap-3">
            <NumberInput
              value={thresholdMinor}
              min={0}
              step={100}
              disabled={!canEdit}
              onChange={setThresholdMinor}
              data-testid={`finance-alert-threshold-minor-${rule.ruleType}`}
            />
            <CurrencyDisplay amount={thresholdMinor} currency="USD" locale={locale} size="sm" />
          </div>
        </div>
      ) : null}

      {showThresholdDays ? (
        <div>
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">
            {t('admin:financial.alertRules.field.thresholdDays')}
          </label>
          <NumberInput
            value={thresholdDays}
            min={0}
            disabled={!canEdit}
            onChange={setThresholdDays}
            data-testid={`finance-alert-threshold-days-${rule.ruleType}`}
          />
        </div>
      ) : null}

      {showSummaryHour ? (
        <div className={isDigest ? 'grid grid-cols-2 gap-3' : undefined}>
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">
              {t('admin:financial.alertRules.field.summaryHour')}
            </label>
            <NumberInput
              value={summaryHour}
              min={0}
              max={23}
              disabled={!canEdit}
              onChange={setSummaryHour}
              data-testid={`finance-alert-summary-hour-${rule.ruleType}`}
            />
          </div>
          {isDigest ? (
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">
                {t('admin:financial.alertRules.field.digestDow')}
              </label>
              <NumberInput
                value={digestDow}
                min={0}
                max={6}
                disabled={!canEdit}
                onChange={setDigestDow}
                data-testid={`finance-alert-dow-${rule.ruleType}`}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {canEdit ? (
        <div className="flex justify-end">
          <Button
            size="sm"
            loading={saving}
            onClick={onSave}
            data-testid={`finance-alert-save-${rule.ruleType}`}
          >
            {saving ? t('admin:financial.alertRules.saving') : t('admin:financial.alertRules.save')}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

export default FinanceAlertRulesPage;
